import React, { useMemo, useState } from "react";

import { useField } from "formik";
import { isEmpty, uniqBy } from "lodash";
import sortBy from "lodash/sortBy";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { addPropertyToBusiness, PropertyAction } from "actions";

import { Autocomplete } from "components/Form/FormikControls";

import { validatePIC } from "lib/PICValidator";
import { getDefaultPropertyId } from "lib/properties";

import {
  getBusinessById,
  getBuyerWayPropertiesByBusinessId,
  getCurrentSaleyardId,
  getProperties,
  getPropertiesByBusinessId,
} from "selectors";

import { useFieldHelpers, useUpdateFormWithOfflineLookup } from "hooks";

const getOptionValue = option => option?.id;

export const PICField = ({
  buyerWayName,
  name,
  onChangeExtra = undefined,
  label,
  businessId,
  suggestedValueFieldName,
  disabled,
  staticOptionLabel,
  addToBusiness = true,
  showAllProperties = false,
  preferBusinessProperties = false,
}) => {
  const { setValue, setError, setTouched } = useFieldHelpers(name);

  const business = useSelector(getBusinessById(businessId));

  const properties = useSelector(getProperties);
  const businessProperties = useSelector(getPropertiesByBusinessId(businessId));
  const businessBuyerWayProperties = useSelector(
    getBuyerWayPropertiesByBusinessId(businessId),
  );

  const saleyardId = useSelector(getCurrentSaleyardId);

  const dispatch = useDispatch();

  const combinedBusinessProperties = useMemo(
    () => [
      ...businessProperties,
      // Include buyer way properties. This may include Properties which are not
      // included in businessProperties (e.g. when a visible buyer way has a related property that is hidden)
      ...(businessBuyerWayProperties || []),
    ],
    [businessProperties, businessBuyerWayProperties],
  );

  // Determine if we should group properties
  const shouldGroupProperties =
    preferBusinessProperties && businessId && showAllProperties;

  // Create a Set of business property IDs to lookup
  const businessPropertyIds = useMemo(
    () => new Set(combinedBusinessProperties.map(p => p.id)),
    [combinedBusinessProperties],
  );

  const stateOptions = useMemo(() => {
    const baseOptions = uniqBy(
      showAllProperties
        ? Object.values(properties)
        : combinedBusinessProperties,
      "PIC",
    );

    // Add group metadata if grouping is enabled
    if (shouldGroupProperties) {
      const optionsWithGroups = baseOptions.map(property => ({
        ...property,
        group: businessPropertyIds.has(property.id)
          ? "Business Properties"
          : "Other Properties",
      }));

      // Sort by group first (Business Properties before Other Properties), then by PIC within each group
      return sortBy(optionsWithGroups, [
        property => (property.group === "Business Properties" ? 0 : 1),
        "PIC",
      ]);
    }

    // Sort by PIC when not grouping
    return sortBy(baseOptions, "PIC");
  }, [
    showAllProperties,
    properties,
    combinedBusinessProperties,
    shouldGroupProperties,
    businessPropertyIds,
  ]);

  useUpdateFormWithOfflineLookup(name);
  const [tempOptions, setTempOptions] = useState([]);

  const addNewProperty = PIC => {
    const uppercasePIC = PIC.toUpperCase();
    if (!validatePIC(uppercasePIC)) {
      const tempInvalidOption = { PIC: uppercasePIC, hide: true };
      // Add a temp options that is not visible in the list so we dont get warnings about the
      // selected option not existing from material ui.
      setTempOptions([...tempOptions, tempInvalidOption]);
      setTouched(true, false)
        .then(() => setValue(tempInvalidOption, false))
        .then(() => setError("Invalid PIC", false));
      return;
    }

    // Check if we're trying to add to business but no business is selected
    if (addToBusiness && isEmpty(business)) {
      const tempInvalidOption = { PIC: uppercasePIC, hide: true };
      setTempOptions([...tempOptions, tempInvalidOption]);
      setTouched(true, false)
        .then(() => setValue(tempInvalidOption, false))
        .then(() => setError("Please select a business first", false));
      return;
    }

    // For now just add PIC, but we could just as easily open another modal.
    const propertyId =
      Object.values(properties).find(p => p.PIC === uppercasePIC)?.id ||
      uuidv4();

    if (addToBusiness) {
      dispatch(
        addPropertyToBusiness(
          {
            id: propertyId,
            PIC: uppercasePIC,
            name: "",
          },
          business.id,
        ),
      );
    } else {
      dispatch(
        PropertyAction.create({
          id: propertyId,
          PIC: uppercasePIC,
          name: "",
        }),
      );
    }

    setValue(propertyId, true);
  };

  const options = useMemo(
    () => uniqBy([...stateOptions, ...tempOptions], "PIC"),
    [stateOptions, tempOptions],
  );

  const getOptionLabel = option =>
    staticOptionLabel ||
    `${
      getDefaultPropertyId(business, saleyardId, buyerWayName) === option.id
        ? "* "
        : ""
    }${[(option.PIC || "").toUpperCase(), option.name]
      .filter(Boolean)
      .join(" - ")}` ||
    "";

  // Group by function for Autocomplete
  const getGroupBy = option => option.group;

  return (
    <Autocomplete
      name={name}
      clearOnBlur
      selectOnFocus
      handleHomeEndKeys
      label={label}
      options={options}
      onChangeExtra={onChangeExtra}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      handleNew={addNewProperty}
      suggestedValueFieldName={suggestedValueFieldName}
      disabled={disabled}
      disableAutoComplete
      groupBy={shouldGroupProperties ? getGroupBy : undefined}
    />
  );
};

export const BusinessPICField = ({
  businessField,
  buyerWayName,
  disabled = false,
  label,
  name,
  onChangeExtra = undefined,
  staticOptionLabel,
}) => {
  const [{ value: businessId }] = useField(businessField);
  return (
    <PICField
      buyerWayName={buyerWayName}
      name={name}
      onChangeExtra={onChangeExtra}
      label={label}
      businessId={businessId}
      key={businessId}
      disabled={disabled}
      staticOptionLabel={staticOptionLabel}
    />
  );
};
